/**
 * Credentials page — manages data source connections.
 *
 * PR 7 additions:
 *  - CredentialExpiryBanner: passive amber banner for credentials expiring within 30 days.
 *    Computed client-side from credential.apiKeyExpiresAt. Reconnect CTA opens HarvestModal.
 *    Dismissal is per-session only (not persisted).
 *
 * Mockup: designs/ai-dashboard-platform-v3/2026-04-23-28-credentials-page-expiry-banner-v3.html
 * Brand guide: §23
 */

import { useEffect, useState } from 'react'
import { PageTitle } from "@/components/custom/PageTitle"
import { useNavigate } from "react-router-dom"
import { DataTable } from "./CredentialsTable"
import { columns } from "./columns"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "@/redux"
import { getPageButtonText } from "@/helpers/button"
import { Pagination } from "@/components/custom/Pagination/Pagination"
import { getCredentials } from "@/services/credentialsService"
import { CredentialExpiryBanner } from './CredentialExpiryBanner'
import { computeDaysUntilExpiry, getExpiryTier } from './expiryUtils'
import { HarvestModal } from '@/pages/Dashboards/components/harvest/HarvestModal'
import {
  dashboardCreationActions,
  selectHarvestFailure,
} from '@/redux/slice/dashboardCreationSlice'
import { startHarvestThunk } from '@/services/HarvestService'

export const Credentials = () => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { userData, token } = useSelector((state: RootState) => state.user)
  const { credentials, pagination } = useSelector((state: RootState) => state.credentials)
  const harvestFailure = useSelector(selectHarvestFailure)

  // Per-session dismissed banner IDs — reappears on page reload, not persisted
  const [dismissedBannerIds, setDismissedBannerIds] = useState<Set<string>>(new Set())

  // Harvest modal state — controls whether the modal is open (for reconnect CTA)
  const [isHarvestModalOpen, setIsHarvestModalOpen] = useState(false)

  // Credentials that have an expiry tier of 'warning' or 'expired' and are not dismissed.
  // Gate on type-declared capability (tracksExpiry) rather than app-name — this auto-extends
  // to future vendors and removes the AppName.CAMPMINDER literal. (Slice 1.4)
  const expiringCredentials = credentials
    .filter(cred => cred.tracksExpiry)
    .filter(cred => {
    if (dismissedBannerIds.has(cred.id)) return false
    const days = computeDaysUntilExpiry(cred.apiKeyExpiresAt)
    const tier = getExpiryTier(days)
    return tier !== 'none'
  })

  // Reconnect — mirrors the Canvas.tsx handleReconnect pattern from PR #317:
  //  1. Clear any stale harvest state so a prior failure doesn't resurface.
  //  2. Await startHarvestThunk — if it fails, harvestFailure is set synchronously
  //     and the useEffect below closes the modal before it ever renders loading.
  //  3. Open the modal only after the thunk has been dispatched.
  const handleReconnect = async () => {
    if (!token) return
    dispatch(dashboardCreationActions.clearHarvest())
    await (dispatch as (action: unknown) => Promise<void>)(
      startHarvestThunk(token, 'campminder')
    )
    setIsHarvestModalOpen(true)
  }

  // Close the modal whenever harvestFailure becomes non-null while open.
  // Covers both post-thunk startup failures AND mid-flow socket failures.
  useEffect(() => {
    if (harvestFailure && isHarvestModalOpen) {
      setIsHarvestModalOpen(false)
    }
  }, [harvestFailure, isHarvestModalOpen])

  const handleDismiss = (credentialId: string) => {
    setDismissedBannerIds(prev => new Set([...prev, credentialId]))
  }

  const handleHarvestClose = () => {
    setIsHarvestModalOpen(false)
    dispatch(dashboardCreationActions.clearHarvest())
  }

  const handleHarvestSuccess = () => {
    setIsHarvestModalOpen(false)
    // Refresh credential list to reflect the updated expiry
    if (token) {
      void dispatch(getCredentials(token, pagination.limit, pagination.offset))
    }
  }

  const handlePageChange = async (newOffset: number) => {
    if (!token) return
    await dispatch(getCredentials(token, pagination.limit, newOffset))
  }

  const handleLimitChange = (newLimit: number) => {
    if (!token) return
    dispatch(getCredentials(token, newLimit, 0))
  }

  return (
    <div className="protectedPageLayout">
      <PageTitle
        title="Credentials"
        buttonText={getPageButtonText(userData?.role, 'Add New Credentials')}
        onButtonClick={() => { navigate('/credentials/create') }}
      />

      {/* Expiry banners — stacked, one per expiring/expired credential (brand guide §23) */}
      {expiringCredentials.map(cred => (
        <CredentialExpiryBanner
          key={cred.id}
          credential={cred}
          onReconnect={handleReconnect}
          onDismiss={handleDismiss}
        />
      ))}

      <DataTable columns={columns} data={credentials} />
      <Pagination
        total={pagination.total}
        limit={pagination.limit}
        offset={pagination.offset}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* HarvestModal — launched inline when user clicks "Reconnect to renew".
          advanceCreationFlowOnSuccess={false} because this is a reconnect flow,
          NOT a dashboard creation flow — we must not mutate the creation FSM. */}
      <HarvestModal
        isOpen={isHarvestModalOpen}
        onClose={handleHarvestClose}
        onSuccess={handleHarvestSuccess}
        onPasteKey={handleHarvestClose}
        advanceCreationFlowOnSuccess={false}
      />
    </div>
  )
}
